import * as React from "react";
import { Check, Wrench, TriangleAlert, ShieldAlert, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const inclusions = ["High-definition video recording of main lines", "Identification of root intrusion and blockages", "Detection of cracked, collapsed, or offset pipes", "Precise location mapping of underground issues", "Digital report with narrated video footage"];
const failurePoints = [{
  id: 1,
  top: "25%",
  left: "30%",
  icon: TriangleAlert,
  label: "Root Intrusion"
}, {
  id: 2,
  top: "60%",
  left: "75%",
  icon: Wrench,
  label: "Pipe Corrosion"
}, {
  id: 3,
  top: "45%",
  left: "45%",
  icon: ShieldAlert,
  label: "Structural Cracks"
}];
export default function UniversityOverview() {
  return <section data-section-id="248294" id="overview" className="bg-background text-foreground py-32 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        <div className="mb-16 max-w-2xl">
          <Badge variant="outline" className="mb-4 border-primary text-primary rounded-md uppercase tracking-widest">
            Advanced Diagnostics
          </Badge>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            Sewer Camera Inspection
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Our state-of-the-art fiber optic cameras provide a clear view into your plumbing's hidden infrastructure, revealing the exact condition of your underground pipes.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div className="relative z-10">
            <div className="absolute -inset-1 bg-gradient-to-br from-primary/10 to-transparent blur-2xl -z-10 rounded-xl opacity-30" />
            <Card className="bg-card border-border rounded-lg shadow-xl overflow-hidden relative">
              {}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/30" />
              
              <CardHeader className="pb-8 pt-10 px-8 sm:px-10">
                <div className="flex justify-between items-start mb-4">
                  <CardTitle className="text-3xl sm:text-4xl font-default font-bold text-foreground">
                    Full Line Analysis
                  </CardTitle>
                  <Wrench className="size-8 text-primary opacity-80" strokeWidth={1.5} />
                </div>
                <p className="text-muted-foreground font-default text-base">
                  A non-invasive diagnostic service that eliminates guesswork and prevents unnecessary excavation by pinpointing plumbing failures.
                </p>
              </CardHeader>
              
              <CardContent className="px-8 sm:px-10 pb-10">
                <Separator className="bg-border mb-8" />
                <ul className="space-y-5">
                  {inclusions.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-4 group">
                      <div className="mt-1 bg-primary/10 p-1 rounded-sm group-hover:bg-primary/20 transition-colors">
                        <Check className="size-5 text-primary" strokeWidth={2} />
                      </div>
                      <span className="text-foreground/80 font-default leading-relaxed">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </CardContent>

              <CardFooter className="px-8 sm:px-10 pb-10 pt-0">
                <Button className="w-full rounded-md h-14 text-base font-semibold tracking-wide group bg-primary text-primary-foreground hover:bg-primary/90" size="lg" asChild>
                  <a href="/SewerCameraInspectionPage">
                    Book Inspection
                    <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </CardFooter>
            </Card>
          </div>

          {}
          <div className="relative w-full aspect-square lg:aspect-[4/5] bg-muted/20 border border-border rounded-lg p-4 sm:p-8 flex items-center justify-center overflow-hidden group">
            {}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px]" />
            
            <div className="relative w-full h-full max-w-md mx-auto">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/sewer_camera_diagram.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/sewer_camera_diagram.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/sewer_camera_diagram.png-wvc-sizes" alt="Sewer camera inspection technical diagram" className="w-full h-full object-contain opacity-90 mix-blend-multiply group-hover:mix-blend-normal transition-all duration-700" loading="lazy" />
              
              {}
              {failurePoints.map((point, i) => {
              const Icon = point.icon;
              return <div key={point.id} data-index={i} className="absolute flex flex-col items-center gap-2 -translate-x-1/2 -translate-y-1/2 group/point cursor-crosshair" style={{
                top: point.top,
                left: point.left
              }}>
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-20" />
                      <div className="relative bg-background border border-primary p-2 rounded-full shadow-md">
                        <Icon className="size-5 text-primary" strokeWidth={2} />
                      </div>
                    </div>
                    <div className="opacity-0 group-hover/point:opacity-100 transition-opacity absolute top-full mt-2 whitespace-nowrap bg-secondary text-secondary-foreground text-xs font-mono px-3 py-1.5 border border-border rounded-md shadow-lg z-20 pointer-events-none">
                      {point.label}
                    </div>
                  </div>;
            })}
            </div>
            
            {}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary/30 m-4" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary/30 m-4" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary/30 m-4" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary/30 m-4" />
          </div>

        </div>
      </div>
    </section>;
}